/** @jsxImportSource @emotion/react */
import { useDroppable } from '@dnd-kit/core';
import { css } from '@emotion/react';

import { useProjectStateLifecycle } from '@/Client/Layout/useProjectStateLifecycle';
import { DEFAULT_NEEDS_ATTENTION_TITLE } from '@/Client/Lib/scopeAttentionFilterLib';
import {
  type ColorSwatch,
  getColorStyle,
  getPartCostValue,
  getTaskTitle,
} from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { formatUsd } from '@/Client/Presenters';
import { useAuth, useFeatureFlags, useScanEditingContextSlice } from '@/Client/State';
import { styleguideColors as COLORS, styleguideFontWeight as WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import type { OverallTaskStatus } from '@kaiizen/kai-pro-api';
import type { ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';

import { pxToRem } from '@/Client/Styles/pxToRem';
import {
  KEBAB_GUTTER_HOVER_STYLE,
  KEBAB_GUTTER_OPEN_STYLE,
  KEBAB_GUTTER_TRANSITION_STYLE,
  KEBAB_HOST_ROW_PROPS,
  KEBAB_MENU_OPEN_RAISE_STYLE,
  ModifierTag,
  NeedsAttentionIndicator,
  TaskStatusBadge,
} from '@/Client/Widgets';
import { KaiNavRowMenu } from './KaiNavRowMenu';
import {
  canApproveTurnBasedDraftTask,
  KaiNavTaskApprovalWidget,
  taskOffersApproval,
} from './KaiNavTaskApproval';
import {
  amountStyle,
  DEFAULT_TASK_COST_LABEL,
  MUTED_TEXT_STYLE,
  selectableRowStyle,
  TRAILING_STACK_STYLE,
} from './navigatorStyles';
import { selectRowOnEnterOrSpace } from './rowSelection';

/* ── task row ──
   A task's summary (its name and status) and its trailing cluster (amount and
   controls). The checkbox, category chip and photo button that used to share
   this row are gone — they turned the row into a control panel when its job
   is to be scanned.

   The row is a div with role="button" rather than a <button>: the approve
   control it carries in review is a real button, and a button inside a button
   is invalid HTML. Keyboard selection is therefore explicit. */

const TASK_ROW_STYLE = css`
  width: 100%;
  min-width: 0;
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 4px 12px;
  padding: 10px 16px;
  border: 0;
  border-bottom: 1px solid ${COLORS.strokeFaded};
  background: ${COLORS.white};
  color: ${COLORS.ink};
  font: inherit;
  text-align: left;

  &:focus-visible { outline: 2px solid ${COLORS.ink}; outline-offset: -2px; }
`;

/** A dragged photo is hovering this row — same green convention as the old
 *  ScanEditor's InlineTaskEditorTaskBar drop target. */
const TASK_ROW_DROP_OVER_STYLE = css`
  background: var(--textGreen2);
`;

/** Tasks wear a heavier accent bar than the group and scope rows above them. */
const TASK_ACCENT_BAR_WIDTH = 4;
const TASK_AMOUNT_FONT_SIZE = 14;

const TASK_SUMMARY_STYLE = css`
  grid-column: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TASK_NAME_ROW_STYLE = css`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TASK_NAME_STYLE = css`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${pxToRem(15)};
  line-height: 1.25;
  color: ${COLORS.ink};
`;

/** The name row above it stretches to the column's width (needed for its own
 *  ellipsis truncation) — this opts the badge back out of that stretch so it
 *  hugs its own content instead of spanning the full row. */
/* The status badge and the modifier tags share this row, so it is a row: left as a bare
   `align-self` the badge and the first tag touched, and two tags ran together. */
const TASK_STATUS_WRAP_STYLE = css`
  label: TASK_STATUS_WRAP_STYLE;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
`;

/**
 * Medium to bold, not semibold to bold: no 600 face exists for CircularXX, so
 * 600 resolves to 700 and the two states would render identically.
 */
const taskNameWeight = (selected: boolean) => (selected ? WEIGHT.bold : WEIGHT.medium);

const TASK_TRAILING_STYLE = css`
  grid-column: 2;
  justify-self: end;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const KaiNavTaskRow = ({
  task,
  groupId,
  colorSwatch,
  selected,
  overallStatus,
  needsAttentionFlag,
  modifierLabels = [],
  onSelect,
  onDelete,
  onDuplicate,
}: {
  task: ScanTaskOptimized;
  /** The task's real owning group — always a group, even when this row is shown
   *  under the Contractor tab, since a dropped photo has to assign somewhere real. */
  groupId: string;
  colorSwatch: ColorSwatch;
  selected: boolean;
  /** Resolved once in the framework (see `overallTaskStatusByTaskId`). */
  overallStatus: OverallTaskStatus;
  /** The modifiers this task's line items carry, named by the org (KAIDEV-4206). */
  modifierLabels?: readonly string[];
  /** Resolved once in the framework (see `taskNeedsAttentionFlag`). */
  needsAttentionFlag: boolean;
  onSelect: (taskId: string) => void;
  /** Row actions, absent when the scope is locked or the host offers none. */
  onDelete?: () => void;
  onDuplicate?: () => void;
}) => {
  const label = getTaskTitle(task, 'Untitled task');
  const taskCostValue = getPartCostValue(task);
  const costLabel = formatUsd(taskCostValue);
  const costIsZero = costLabel === DEFAULT_TASK_COST_LABEL;
  const showOverallStatus = overallStatus !== 'approved';
  const { setNodeRef, isOver } = useDroppable({
    id: `navigator-task-${groupId}-${task.id}`,
    data: { type: 'task', groupId, taskId: task.id },
  });

  const projectStateLifecycle = useProjectStateLifecycle();
  const { scanIsChangeOrder, changedScopedTaskIds, project } = useScanEditingContextSlice([
    'scanIsChangeOrder',
    'changedScopedTaskIds',
    'project',
  ]);
  const { userId } = useAuth();
  const { featureFlags } = useFeatureFlags();
  const turnBasedProject = featureFlags.turnBasedHandoff && !!project;
  const canApproveTurnBasedDraft = canApproveTurnBasedDraftTask({
    turnBasedHandoff: turnBasedProject,
    managerIds: project?.managerIds,
    userId,
  });
  const hasRowMenu = !!onDelete || !!onDuplicate;
  const offersApproval = taskOffersApproval(task, {
    projectStateLifecycle,
    scanIsChangeOrder,
    changedScopedTaskIds,
    turnBasedHandoff: turnBasedProject,
    allowTurnBasedDraftApproval: canApproveTurnBasedDraft,
  });
  /** Holds the kebab gutter open only while this row has a kebab to put in it.
   *  The gutter is reserved so every level's approve control lines up at the same
   *  right edge (see KaiNavigator.tsx), but that only holds in review, where group
   *  and scope bulk approval also render. A task still offers approval in closeout,
   *  where bulk approval never does. Once closeout locks the scope there's no kebab
   *  either, so an open gutter would only push this row's amount and approve button
   *  in from every header's right edge (KAIDEV-4275). In review the kebab is always
   *  present (the scope is locked only in closeout), so alignment there is unchanged. */
  const holdsKebabGutterOpen = offersApproval && hasRowMenu;

  return (
    <div
      ref={setNodeRef}
      css={[
        TASK_ROW_STYLE,
        KEBAB_MENU_OPEN_RAISE_STYLE,
        selectableRowStyle({ selected, accentBarWidth: TASK_ACCENT_BAR_WIDTH }),
        isOver && TASK_ROW_DROP_OVER_STYLE,
      ]}
      style={getColorStyle(colorSwatch)}
      role='button'
      tabIndex={0}
      {...KEBAB_HOST_ROW_PROPS}
      data-scope-navigator-task='true'
      data-closeout-color-token={colorSwatch.token}
      data-selected={selected ? 'true' : 'false'}
      aria-label={`Select ${label}`}
      aria-pressed={selected}
      onClick={() => onSelect(task.id)}
      onKeyDown={selectRowOnEnterOrSpace(() => onSelect(task.id))}
    >
      <div css={TASK_SUMMARY_STYLE}>
        <span css={TASK_NAME_ROW_STYLE}>
          <NeedsAttentionIndicator show={needsAttentionFlag} size={13} label={DEFAULT_NEEDS_ATTENTION_TITLE} />
          <span css={[TASK_NAME_STYLE, costIsZero && MUTED_TEXT_STYLE]} style={{ fontWeight: taskNameWeight(selected) }}>{label}</span>
        </span>
        {showOverallStatus || modifierLabels.length
          ? (
            <span css={TASK_STATUS_WRAP_STYLE}>
              {showOverallStatus ? <TaskStatusBadge status={overallStatus} /> : null}
              {
                /* Beside the status badge, which these are shaped after — same height, and
                  rounded because neither is clickable. */
              }
              {modifierLabels.map(modifierLabel => <ModifierTag key={modifierLabel} label={modifierLabel} />)}
            </span>
          )
          : null}
      </div>
      <span
        css={[
          TASK_TRAILING_STYLE,
          KEBAB_GUTTER_TRANSITION_STYLE,
          hasRowMenu && KEBAB_GUTTER_HOVER_STYLE,
          holdsKebabGutterOpen && KEBAB_GUTTER_OPEN_STYLE,
        ]}
      >
        <span css={TRAILING_STACK_STYLE}>
          <span css={[amountStyle(TASK_AMOUNT_FONT_SIZE), costIsZero && MUTED_TEXT_STYLE]}>{costLabel}</span>
          <KaiNavTaskApprovalWidget task={task} />
        </span>
      </span>
      {
        /* A sibling of the trailing cluster, not a child of it: `transform` on that
        cluster makes it a containing block for absolutely-positioned descendants, so a
        kebab nested inside it would slide with it instead of staying pinned to the row's
        own right edge. Grid placement is moot either way — an absolutely-positioned grid
        item is out of flow and doesn't need a column. */
      }
      <KaiNavRowMenu onDelete={onDelete} onDuplicate={onDuplicate} label={label} alwaysVisible={offersApproval} />
    </div>
  );
};
